import { RSAA, ApiError } from 'redux-api-middleware';
import * as Sentry from '@sentry/react';
import { Dispatch } from 'redux';

import backoff from 'redux/helpers/backoff';
import getUsageTrackingUrl from 'url-constructors/getUsageTrackingURL';
import getUniqueObjectsArray from 'helpers/getUniqueObjectsArray';

import { TGetRootState } from 'types/shared';
import { EntityTypes } from 'context/entityContext';
import getRuntimeEnv from 'helpers/env';

const TRACK_ENTITY_VIEWED =
  'OPUS/USAGE_STATISTICS/ENTITY_VIEWED/TRACK_ENTITY_VIEWED';

const SAVE_REQUEST = 'OPUS/USAGE_STATISTICS/ENTITY_VIEWED/SAVE_REQUEST';
const SAVE_RETRY = 'OPUS/USAGE_STATISTICS/ENTITY_VIEWED/SAVE_RETRY';
const SAVE_SUCCESS = 'OPUS/USAGE_STATISTICS/ENTITY_VIEWED/SAVE_SUCCESS';
const SAVE_FAILURE = 'OPUS/USAGE_STATISTICS/ENTITY_VIEWED/SAVE_FAILURE';

const INIT_REQUEST = 'OPUS/USAGE_STATISTICS/ENTITY_VIEWED/INIT_REQUEST';
const INIT_SUCCESS = 'OPUS/USAGE_STATISTICS/ENTITY_VIEWED/INIT_SUCCESS';
const INIT_FAILURE = 'OPUS/USAGE_STATISTICS/ENTITY_VIEWED/INIT_FAILURE';

const LOAD_REQUEST = 'OPUS/USAGE_STATISTICS/ENTITY_VIEWED/LOAD_REQUEST';
const LOAD_SUCCESS = 'OPUS/USAGE_STATISTICS/ENTITY_VIEWED/LOAD_SUCCESS';
const LOAD_FAILURE = 'OPUS/USAGE_STATISTICS/ENTITY_VIEWED/LOAD_FAILURE';

type ViewedEntitiesActionTypes =
  | typeof TRACK_ENTITY_VIEWED
  | typeof SAVE_REQUEST
  | typeof SAVE_RETRY
  | typeof SAVE_SUCCESS
  | typeof SAVE_FAILURE
  | typeof INIT_REQUEST
  | typeof INIT_SUCCESS
  | typeof INIT_FAILURE
  | typeof LOAD_REQUEST
  | typeof LOAD_SUCCESS
  | typeof LOAD_FAILURE;

type RecentlyViewedType = {
  id: string;
  type: EntityTypes;
};

interface IViewedEntityState {
  pending: boolean;
  fulfilled: boolean;
  rejected: boolean;
  recentlyViewed: RecentlyViewedType[];
}

const initialState: IViewedEntityState = {
  pending: false,
  fulfilled: false,
  rejected: false,
  recentlyViewed: [],
};

interface IViewedAction {
  type: ViewedEntitiesActionTypes;
  payload: {
    recentlyViewed: RecentlyViewedType[];
  };
}

const TRACKING_TYPE = 'entityViewed';

export default function reducer(
  state: IViewedEntityState = initialState,
  action: IViewedAction
): IViewedEntityState {
  switch (action.type) {
    case TRACK_ENTITY_VIEWED: {
      return {
        ...state,
        recentlyViewed: getUniqueObjectsArray([
          ...state.recentlyViewed,
          ...action.payload.recentlyViewed,
        ]),
      };
    }

    case SAVE_RETRY: {
      return {
        ...state,
        pending: true,
        fulfilled: false,
      };
    }

    case INIT_SUCCESS:
    case SAVE_SUCCESS: {
      return {
        ...state,
        pending: false,
        fulfilled: true,
        rejected: false,
      };
    }

    case INIT_FAILURE:
    case LOAD_FAILURE:
    case SAVE_FAILURE:
      return {
        ...state,
        pending: false,
        fulfilled: false,
        rejected: true,
      };

    case INIT_REQUEST:
    case LOAD_REQUEST:
    case SAVE_REQUEST:
      return {
        ...state,
        pending: true,
      };

    case LOAD_SUCCESS: {
      return {
        ...state,
        pending: false,
        fulfilled: true,
        rejected: false,
        recentlyViewed:
          getUniqueObjectsArray(action.payload.recentlyViewed) ?? [],
      };
    }

    default:
      return state;
  }
}

/**
 * This action creator initializes an empty object at a specific component_settings endpoint for a user.
 * Only called once per user, when we get a 404 while trying to load the settings.
 */
function initFilterUsageEndpoint(): (
  dispatch: Dispatch,
  getState: TGetRootState
) => Promise<any> {
  return async (dispatch: Dispatch, getState: TGetRootState): Promise<any> => {
    const { id } = getState().authentication.profile;

    const url = getUsageTrackingUrl(id, TRACKING_TYPE);

    // dispatch the action
    return await dispatch({
      [RSAA]: {
        types: [INIT_REQUEST, INIT_SUCCESS, INIT_FAILURE],
        endpoint: url,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          recentlyViewed: [],
        }),
      },
    });
  };
}

export const loadRecentlyViewedEntities = (): ((
  dispatch: Dispatch,
  getState: TGetRootState
) => Promise<any>) => {
  return async function (
    dispatch: Dispatch,
    getState: TGetRootState
  ): Promise<any> {
    const { id } = getState().authentication.profile;
    const url = getUsageTrackingUrl(id, TRACKING_TYPE);

    try {
      const actionResponse = await dispatch({
        [RSAA]: {
          types: [LOAD_REQUEST, LOAD_SUCCESS, LOAD_FAILURE],
          endpoint: url,
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
        },
      });

      // This should only happen once for every user
      if (actionResponse.error) {
        if ((actionResponse.payload as ApiError<any>).status === 404) {
          return await dispatch(initFilterUsageEndpoint() as any);
        }
      }

      return actionResponse;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('There was an error loading your recent filters');
    }
  };
};

/**
 * This action creator reads the store and sends the current state of the store to the KV API
 */
export function saveToApi(
  viewedEntity: RecentlyViewedType
): (dispatch: Dispatch, getState: TGetRootState) => Promise<any> {
  return async function (
    dispatch: Dispatch,
    getState: TGetRootState
  ): Promise<any> {
    const { id } = getState().authentication.profile;
    const url = getUsageTrackingUrl(id, TRACKING_TYPE);
    const { recentlyViewed } = getState().recentlyViewedUsage;
    const uniqueViewedEntities = getUniqueObjectsArray([
      viewedEntity,
      ...recentlyViewed,
    ]);

    try {
      const response = await backoff(
        async () =>
          await dispatch({
            [RSAA]: {
              types: [SAVE_REQUEST, SAVE_SUCCESS, SAVE_RETRY],
              endpoint: url,
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'same-origin',
              body: JSON.stringify({ recentlyViewed: uniqueViewedEntities }),
            },
          }),
        'An error occurred trying to save your filter usage statistics. Retrying for the %s time...'
      );

      if (response.error) {
        throw response.payload;
      }

      return response;
    } catch (error) {
      // The final retry error will end up here. Log it to console.

      // eslint-disable-next-line no-console
      console.warn('Your recent filters failed to be saved remotely.');
      // eslint-disable-next-line no-console
      console.error(error);

      // Report it to Sentry
      if (getRuntimeEnv().NODE_ENV === 'production') {
        // Send the exception to Sentry
        Sentry.captureException(error);
      }

      // Dispatch a final failed state
      dispatch({ type: SAVE_FAILURE });
    }
  };
}

/**
 * This action creator takes an filterType and filterValue
 * It first updates the local reducer before sending off an API request to sync the local redux store to the API
 *
 * @export
 * @param {string} filterType   The type of the filter you want to track
 * @param {string|number} filterValue  The value of the filter you're tracking
 */
export function trackViewedEntities(
  payload: RecentlyViewedType
): (dispatch: Dispatch) => Promise<any> {
  return async function (dispatch: Dispatch): Promise<any> {
    await dispatch(loadRecentlyViewedEntities() as any);

    dispatch({
      type: TRACK_ENTITY_VIEWED,
      payload: { recentlyViewed: [payload] },
    });
    dispatch(saveToApi(payload) as any);
  };
}
